import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRightIcon,
  ClipboardDocumentListIcon,
  DocumentArrowDownIcon,
  InboxArrowDownIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { loteAfiliacionSepService, type EstadoLoteSep, type LoteSepResumido } from '../../services/api';
import { formatFechaCalendario } from '../../utils/fechas';
import { numeroCorto } from '../../utils/lotesAfiliacion';
import { ESTADO_LOTE_SEP, claseDemora, diasDesde } from '../../utils/lotesAfiliacionSep';

type Pestania = 'papel' | 'listos' | 'verificacion' | 'resueltos';

const PESTANIAS: { clave: Pestania; titulo: string; descripcion: string; estados: EstadoLoteSep[]; accion: string }[] = [
  {
    clave: 'papel',
    titulo: 'Esperando el papel',
    descripcion: 'El partido presentó el lote en el sistema. Falta que traiga las fichas en papel a Mesa de Entradas.',
    estados: ['PRESENTADO'],
    accion: 'Registrar recepción',
  },
  {
    clave: 'listos',
    titulo: 'Listos para verificar',
    descripcion: 'Ya se recibieron las fichas en papel. Falta abrir la verificación de autenticidad.',
    estados: ['FICHAS_RECIBIDAS'],
    accion: 'Abrir verificación',
  },
  {
    clave: 'verificacion',
    titulo: 'En verificación',
    descripcion: 'La verificación está en curso según la Res. 011/2026.',
    estados: ['EN_VERIFICACION', 'EN_SUBSANACION'],
    accion: 'Ver lote',
  },
  {
    clave: 'resueltos',
    titulo: 'Resueltos',
    descripcion: 'La verificación terminó y el lote quedó resuelto.',
    estados: ['RESUELTO'],
    accion: 'Ver lote',
  },
];

const PASOS_CIRCUITO = [
  { icono: DocumentArrowDownIcon, titulo: 'El partido presenta', texto: 'Sube el lote al sistema y se crea el expediente.' },
  { icono: InboxArrowDownIcon, titulo: 'Mesa de Entradas recibe', texto: 'Marca las fichas en papel que llegaron y les da número.' },
  { icono: ShieldCheckIcon, titulo: 'La SEP verifica', texto: 'Abre la verificación de autenticidad sobre las fichas recibidas.' },
];

export default function LotesAfiliacionSepPage() {
  const navigate = useNavigate();
  const [pestania, setPestania] = useState<Pestania>('papel');
  const [busqueda, setBusqueda] = useState('');

  const { data: lotes = [], isLoading, isError } = useQuery({
    queryKey: ['lotes-afiliacion-sep'],
    queryFn: () => loteAfiliacionSepService.listar(),
  });

  const actual = PESTANIAS.find(p => p.clave === pestania)!;
  const cantidad = (p: typeof PESTANIAS[number]) => lotes.filter(l => p.estados.includes(l.estado)).length;

  const visibles = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return lotes
      .filter(l => actual.estados.includes(l.estado))
      .filter(l => !texto || [l.partido.denominacion, l.partido.sigla, l.numero, l.expediente?.numero]
        .some(v => v?.toLowerCase().includes(texto)));
  }, [lotes, actual, busqueda]);

  return (
    <div className="space-y-6">
      <div>
        <span className="mb-2 inline-block rounded-full bg-orange-100 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-orange-800">
          Solo uso interno
        </span>
        <h1 className="text-2xl font-bold text-gray-900">Lotes de afiliación</h1>
        <p className="text-sm text-gray-500">Registro de afiliados presentados por los partidos (Art. 32, Ley XII N° 23).</p>
      </div>

      <ol className="grid gap-3 sm:grid-cols-3">
        {PASOS_CIRCUITO.map((p, i) => (
          <li key={p.titulo} className="card flex items-start gap-3 px-4 py-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-50">
              <p.icono className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{i + 1}. {p.titulo}</p>
              <p className="text-xs text-gray-500">{p.texto}</p>
            </div>
          </li>
        ))}
      </ol>

      <div className="card">
        <div className="flex gap-1 overflow-x-auto border-b border-gray-200 px-2" role="tablist">
          {PESTANIAS.map(p => {
            const n = cantidad(p);
            const activa = p.clave === pestania;
            return (
              <button
                key={p.clave}
                role="tab"
                aria-selected={activa}
                onClick={() => setPestania(p.clave)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium ${
                  activa ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {p.titulo}
                <span className={`rounded-full px-2 py-0.5 text-xs ${activa ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'}`}>
                  {n}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <p className="text-sm text-gray-600">{actual.descripcion}</p>
          <div className="relative w-full sm:w-72">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              placeholder="Buscar partido, lote o expediente"
              className="input pl-9"
            />
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary-600"></div>
        </div>
      )}

      {isError && <div className="alert-danger text-sm">No se pudieron cargar los lotes. Probá recargar la página.</div>}

      {!isLoading && !isError && visibles.length === 0 && (
        <div className="card flex flex-col items-center px-6 py-12 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50">
            <ClipboardDocumentListIcon className="h-6 w-6 text-primary-600" />
          </div>
          <p className="mt-4 font-medium text-gray-900">
            {busqueda ? 'Ningún lote coincide con la búsqueda' : `No hay lotes en "${actual.titulo}"`}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {busqueda ? 'Probá con otro nombre o número.' : 'Cuando haya alguno, va a aparecer acá.'}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {visibles.map(lote => (
          <TarjetaLoteSep
            key={lote.id}
            lote={lote}
            accion={actual.accion}
            onAbrir={() => navigate(`/lotes-afiliacion-sep/${lote.id}`)}
          />
        ))}
      </div>
    </div>
  );
}

function TarjetaLoteSep({ lote, accion, onAbrir }: { lote: LoteSepResumido; accion: string; onAbrir: () => void }) {
  const estado = ESTADO_LOTE_SEP[lote.estado];
  const esperandoPapel = lote.estado === 'PRESENTADO';
  const dias = esperandoPapel ? diasDesde(lote.fechaPresentacion) : null;
  const porcentaje = lote.totalFichas ? Math.round((lote.fichasRecibidas / lote.totalFichas) * 100) : 0;

  return (
    <button
      onClick={onAbrir}
      className="card block w-full text-left transition hover:border-primary-300 hover:shadow-md"
    >
      <div className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">{lote.partido.denominacion}</h3>
            {lote.partido.sigla && <span className="text-sm text-gray-400">({lote.partido.sigla})</span>}
            <span className={estado.clase}>{estado.etiqueta}</span>
          </div>
          <p className="text-sm text-gray-500">
            Lote N° {numeroCorto(lote.numero)}
            {lote.expediente && <> · Expediente <span className="font-medium text-gray-700">{lote.expediente.numero}</span></>}
            {' · '}Presentado el {formatFechaCalendario(lote.fechaPresentacion)}
          </p>
          {esperandoPapel ? (
            <div className="flex flex-wrap items-center gap-2 pt-1 text-sm">
              <span className="text-gray-600">{lote.totalFichas} {lote.totalFichas === 1 ? 'ficha' : 'fichas'} por recibir</span>
              {dias !== null && (
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${claseDemora(dias)}`}>
                  {dias === 0 ? 'Presentado hoy' : `Hace ${dias} ${dias === 1 ? 'día' : 'días'}`}
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 pt-1">
              <div className="h-1.5 w-32 overflow-hidden rounded-full bg-gray-200">
                <div className="h-full bg-primary-600" style={{ width: `${porcentaje}%` }} />
              </div>
              <span className="text-sm text-gray-600">
                {lote.fichasRecibidas} de {lote.totalFichas} fichas recibidas
                {lote.fechaRecepcionFichas && ` el ${formatFechaCalendario(lote.fechaRecepcionFichas)}`}
              </span>
            </div>
          )}
        </div>
        <span className="inline-flex flex-shrink-0 items-center gap-1 text-sm font-medium text-primary-600">
          {accion} <ArrowRightIcon className="h-4 w-4" />
        </span>
      </div>
    </button>
  );
}
